import {
  Box,
  BoxProps,
  IconButton,
  Drawer,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useDisclosure,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { FC, useRef, useState } from 'react';

import DrawerIcon from '@/assets/icons/ic-drawer.svg';
import NotificationIcon from '@/assets/icons/ic-notification.svg';
import PeopleIcon from '@/assets/icons/ic-people.svg';

import { ChatPopup } from '@/components/ui/shared/chat/ChatPopup/ChatPopup';
import { NotificationBadge } from '@/components/ui/shared/notifications/NotificationBadge/NotificationBadge';
import { Notifications } from '@/components/ui/shared/notifications/Notifications/Notifications';
import { ErrorFallback } from '@/components/ui/shared/overlays/ErrorFallback/ErrorFallback';
import { BugsnagErrorBoundary } from '@/components/ui/shared/utilities/BugsnagErrorBoundary/BugsnagErrorBoundary';
import { ResourceType } from '@/enums/ResourceType';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { eventConfigurationQueryOptions } from '@/queries/upgrade/eventConfiguration';
import { BELL_RESOURCE_TYPES } from '@/queries/upgrade/notifications';

export const SecondaryMenuMobile: FC<BoxProps> = ({ ...rest }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const btnRef = useRef();

  const { eventId } = useQueryParams<{ eventId: string }>({
    eventId: StringParam,
  });

  const { data: eventConfiguration } = useQuery(eventConfigurationQueryOptions(eventId));

  return (
    <Box {...rest}>
      <IconButton
        ref={btnRef}
        aria-label="Open navigation drawer"
        onClick={onOpen}
        variant="nav"
        w="3.5rem"
        ml="auto"
        icon={
          <NotificationBadge resourceType={[...BELL_RESOURCE_TYPES, ResourceType.EventChatChannel]}>
            <DrawerIcon />
          </NotificationBadge>
        }
      />

      <Drawer
        isOpen={isOpen}
        blockScrollOnMount={false}
        placement="right"
        onClose={onClose}
        finalFocusRef={btnRef}
        size="full"
      >
        <DrawerOverlay>
          {/* $100vh is Chakra's 100dvh (with fallbacks): on phones plain 100vh is the height with the browser
              bars hidden, so the drawer ran taller than the visible screen and the chat input sat under them */}
          <DrawerContent h="$100vh" maxH="$100vh">
            <Tabs
              variant="nav"
              isLazy
              defaultIndex={selectedTabIndex}
              onChange={setSelectedTabIndex}
              display="flex"
              flexDirection="column"
              flex={1}
              minH={0}
            >
              <TabList>
                {eventConfiguration?.direct_messaging && (
                  <Tab>
                    <NotificationBadge refreshInterval={30_000} resourceType={ResourceType.EventChatChannel}>
                      <PeopleIcon />
                    </NotificationBadge>
                  </Tab>
                )}
                <Tab>
                  <NotificationBadge resourceType={BELL_RESOURCE_TYPES}>
                    <NotificationIcon />
                  </NotificationBadge>
                </Tab>
                <DrawerCloseButton ml="auto" alignSelf="center" />
              </TabList>

              <TabPanels flex={1} minH={0}>
                {eventConfiguration?.direct_messaging && (
                  // fills what the tab list leaves, rather than guessing the tab list's height
                  <TabPanel h="full" p={0}>
                    <ChatPopup />
                  </TabPanel>
                )}
                <TabPanel p={0}>
                  <BugsnagErrorBoundary FallbackComponent={ErrorFallback}>
                    <Notifications />
                  </BugsnagErrorBoundary>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </DrawerContent>
        </DrawerOverlay>
      </Drawer>
    </Box>
  );
};
